import { useCallback, useEffect, useState } from "react";

import type { Proyecto } from "./api/types";
import { listarProyectos } from "./api/client";
import { FormularioNuevoProyecto } from "./components/FormularioNuevoProyecto";
import { ListaProyectos } from "./components/ListaProyectos";
import { Login } from "./components/Login";
import { VistaProyecto } from "./components/VistaProyecto";
import { useSesion } from "./hooks/useSesion";
import estilos from "./App.module.css";

export function App(): JSX.Element {
  const sesion = useSesion();
  const [proyectoSeleccionado, setProyectoSeleccionado] = useState<
    string | null
  >(null);
  const [proyectos, setProyectos] = useState<Proyecto[]>([]);
  const [vista, setVista] = useState<"proyectos" | "nuevo">("proyectos");

  const refrescarProyectos = useCallback((): void => {
    if (!sesion.token) return;
    listarProyectos(sesion.token)
      .then((lista) => {
        const ordenados = [...lista].sort((a, b) =>
          b.updated_at.localeCompare(a.updated_at),
        );
        setProyectos(ordenados);
      })
      .catch(() => {
        // Backend inalcanzable o sesion invalida: un 401 ya dispara el cierre
        // de sesion via el manejador central de client.ts.
      });
  }, [sesion.token]);

  // Refresca la lista al entrar a la app y cada vez que se vuelve de la
  // vista de detalle (puede haber cambiado el estado de algun proyecto).
  useEffect(() => {
    refrescarProyectos();
  }, [refrescarProyectos, proyectoSeleccionado]);

  const alCrearProyecto = (proyecto: Proyecto): void => {
    setProyectos((prev) => [proyecto, ...prev]);
    setProyectoSeleccionado(proyecto.id);
  };

  const alActualizarProyecto = (proyecto: Proyecto): void => {
    setProyectos((prev) => {
      const existe = prev.some((p) => p.id === proyecto.id);
      if (!existe) return [proyecto, ...prev];
      return prev.map((p) => (p.id === proyecto.id ? proyecto : p));
    });
  };

  if (!sesion.token) {
    return (
      <Login
        cargando={sesion.cargando}
        error={sesion.error}
        onIniciarSesion={sesion.iniciarSesion}
        onRegistrar={sesion.registrar}
      />
    );
  }

  return (
    <div className={estilos.app}>
      <header className={estilos.encabezado}>
        <div className={estilos.marca}>
          <div className={estilos.logo} aria-hidden="true">
            S
          </div>
          <div>
            <h1>Super Marketing</h1>
            <p>Generador agentico de flyers editables</p>
          </div>
        </div>
        <div className={estilos.acciones}>
          {sesion.usuario && (
            <span className={estilos.usuario}>{sesion.usuario.email}</span>
          )}
          <button
            type="button"
            className="boton boton-secundario"
            onClick={sesion.cerrarSesion}
          >
            Cerrar sesion
          </button>
        </div>
      </header>

      <main className={estilos.cuerpo}>
        {proyectoSeleccionado ? (
          <div className={estilos.contenido}>
            <div>
              <button
                type="button"
                className="boton boton-secundario"
                onClick={() => setProyectoSeleccionado(null)}
              >
                Volver a proyectos
              </button>
            </div>
            <VistaProyecto
              key={proyectoSeleccionado}
              token={sesion.token}
              proyectoId={proyectoSeleccionado}
              onProyectoActualizado={alActualizarProyecto}
            />
          </div>
        ) : (
          <div className={estilos.contenido}>
            <div
              className={estilos.tabsInicio}
              role="tablist"
              aria-label="Vista de inicio"
            >
              <button
                type="button"
                role="tab"
                aria-selected={vista === "proyectos"}
                className={`${estilos.tabInicio} ${vista === "proyectos" ? estilos.tabInicioActivo : ""}`}
                onClick={() => setVista("proyectos")}
              >
                Proyectos
              </button>
              <button
                type="button"
                role="tab"
                aria-selected={vista === "nuevo"}
                className={`${estilos.tabInicio} ${vista === "nuevo" ? estilos.tabInicioActivo : ""}`}
                onClick={() => setVista("nuevo")}
              >
                + Nuevo flyer
              </button>
            </div>
            {vista === "proyectos" ? (
              <ListaProyectos
                proyectos={proyectos}
                onAbrir={setProyectoSeleccionado}
                onNuevo={() => setVista("nuevo")}
              />
            ) : (
              <FormularioNuevoProyecto
                token={sesion.token}
                onCreado={alCrearProyecto}
              />
            )}
          </div>
        )}
      </main>

      <footer className={estilos.pie}>
        Super Marketing · Generador agentico de flyers
      </footer>
    </div>
  );
}
